import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  createBooking,
  listAvailableSlots,
  listDoctors,
} from '../../api/bookings.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import {
  addDays,
  firstError,
  pngDayKey,
  pngDayLabel,
  pngDayToUtcRange,
  pngTime,
} from '../../utils/format.js'

/**
 * Patient — Book a consultation.
 *
 * Two-stage flow on a single page (progressive disclosure rather than
 * separate routes — fewer back-button surprises on slow PNG networks):
 *
 *   Stage A — Find a doctor
 *     Filter the doctor list by user_type (Doctor / Specialist) and
 *     by free-text specialty (GP / Cardiology / Surgery / ED / etc.).
 *     Each row links to stage B for that doctor.
 *
 *   Stage B — Pick a date and slot
 *     Date picker (HTML5) + a list of "Slots Available" for that
 *     date, server-filtered to exclude slots already booked by other
 *     patients. Tap a slot → modal to confirm and (optionally) add a
 *     reason for the visit. Submit → atomic booking → patient lands
 *     on /patient/bookings to see the new appointment.
 */
export default function BookConsultation() {
  const [selectedDoctor, setSelectedDoctor] = useState(null)
  // A booking started from the AI-triage chat carries ?triage=<intent id>.
  // We thread it into createBooking so the RAG grade + clinician summary
  // attach to this appointment (surfaces on the doctor's queue).
  const [searchParams] = useSearchParams()
  const triageIntentId = searchParams.get('triage') || null

  if (selectedDoctor) {
    return (
      <SlotPicker
        doctor={selectedDoctor}
        onBack={() => setSelectedDoctor(null)}
        triageIntentId={triageIntentId}
      />
    )
  }
  return <DoctorBrowse onPick={setSelectedDoctor} triageIntentId={triageIntentId} />
}

// ---------------------------------------------------------------------------
// Stage A — Doctor browse + filter
// ---------------------------------------------------------------------------

function DoctorBrowse({ onPick, triageIntentId = null }) {  // eslint-disable-line no-unused-vars
  const [userType, setUserType] = useState('')
  const [specialty, setSpecialty] = useState('')
  const [search, setSearch] = useState('')

  const q = useQuery({
    queryKey: ['doctor-browse', userType, specialty, search],
    // Only telehealth-capable doctors are bookable for a consult.
    queryFn: () => listDoctors({ userType, specialty, search, telehealth: true }),
  })
  const rows = q.data || []

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Book a consultation"
          subtitle="Pick a doctor to see their available appointment slots."
        />
        <div className="grid gap-3 sm:grid-cols-3">
          <Labeled label="Type">
            <select
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm bg-white"
            >
              <option value="">Any (GP or Specialist)</option>
              <option value="doctor">General Practitioner</option>
              <option value="specialist">Specialist</option>
            </select>
          </Labeled>
          <Labeled label="Specialty">
            <input
              value={specialty}
              onChange={(e) => setSpecialty(e.target.value)}
              placeholder="e.g. Cardiology, Surgery, Emergency"
              className="border rounded px-2 py-1.5 text-sm"
            />
          </Labeled>
          <Labeled label="Search">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Doctor name or place of work"
              className="border rounded px-2 py-1.5 text-sm"
            />
          </Labeled>
        </div>
      </Card>

      <Card>
        <CardHeader
          title={`${rows.length} doctor${rows.length === 1 ? '' : 's'}`}
          subtitle="Tap a doctor to see when they can see you."
        />
        {q.error && <ErrorBanner error={q.error} onRetry={() => q.refetch()} />}
        {q.isLoading ? (
          <div className="text-sm text-slate-400">Loading doctors…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-400 italic">
            No doctors match these filters. Clear them and try again.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((d) => (
              <li key={d.id} className="py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">
                    Dr {d.full_name || d.username}
                  </div>
                  <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    <span className="font-medium">
                      {d.user_type === 'specialist' ? 'Specialist' : 'GP'}
                    </span>
                    {d.specialty && <span>{d.specialty}</span>}
                    {d.place_of_work && <span>· {d.place_of_work}</span>}
                    {d.years_of_experience != null && (
                      <span>· {d.years_of_experience} yr exp.</span>
                    )}
                    {d.consultation_fee != null && d.consultation_fee !== '' && (
                      <span>· PGK {d.consultation_fee}</span>
                    )}
                  </div>
                  {d.bio && (
                    <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                      {d.bio}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onPick(d)}
                  className="shrink-0 self-center text-xs font-semibold bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-1.5"
                >
                  See slots
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Stage B — Date + slot picker for one doctor
// ---------------------------------------------------------------------------

function SlotPicker({ doctor, onBack, triageIntentId = null }) {
  const qc = useQueryClient()
  const nav = useNavigate()

  // Default date — tomorrow in PNG, mirroring the doctor's editor default.
  const [date, setDate] = useState(() => addDays(pngDayKey(), 1))

  // The date key is a PNG calendar day; the server stores UTC. Translate the
  // PNG-day boundary to UTC ISO so the filter returns exactly that day's
  // slots regardless of the patient's device timezone.
  const utcRange = useMemo(() => pngDayToUtcRange(date), [date])

  const slotsQ = useQuery({
    queryKey: ['available-slots', doctor.id, date],
    queryFn: () => listAvailableSlots({
      doctorId: doctor.id,
      dateFrom: utcRange.start,
      dateTo:   utcRange.end,
      availableOnly: true,
    }),
    enabled: Boolean(doctor.id && date),
  })
  const slots = useMemo(
    () => (slotsQ.data || []).slice().sort(
      (a, b) => new Date(a.slot_at) - new Date(b.slot_at)
    ),
    [slotsQ.data],
  )

  // Confirmation modal state.
  const [pickedSlot, setPickedSlot] = useState(null)
  const [reason, setReason] = useState('')

  const book = useMutation({
    mutationFn: () => createBooking({
      slotId: pickedSlot.id,
      reason: reason.trim(),
      triageIntentId,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['available-slots', doctor.id, date] })
      qc.invalidateQueries({ queryKey: ['my-bookings'] })
      // Land on the patient's bookings list so they see the new
      // appointment with the cancel option already at hand.
      nav('/patient/bookings', { replace: true })
    },
  })

  function nextDay(direction) {
    setDate(addDays(date, direction))
  }

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Booking with</div>
            <div className="text-lg font-semibold">Dr {doctor.full_name || doctor.username}</div>
            <div className="text-xs text-slate-500">
              {doctor.user_type === 'specialist' ? 'Specialist' : 'GP'}
              {doctor.specialty && ` · ${doctor.specialty}`}
              {doctor.place_of_work && ` · ${doctor.place_of_work}`}
            </div>
          </div>
          <button
            onClick={onBack}
            className="text-xs text-wantok-700 underline shrink-0"
          >
            ← Choose another doctor
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => nextDay(-1)}
            className="text-xs border border-slate-200 hover:border-wantok-400 rounded px-2 py-1.5"
            title="Previous day"
          >
            ‹ Prev
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm"
          />
          <button
            onClick={() => nextDay(1)}
            className="text-xs border border-slate-200 hover:border-wantok-400 rounded px-2 py-1.5"
            title="Next day"
          >
            Next ›
          </button>
          <span className="text-xs text-slate-500 ml-auto">
            {pngDayLabel(date)}
          </span>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Slots available"
          subtitle={
            slotsQ.isLoading
              ? 'Loading…'
              : slots.length === 0
                ? "No open slots on this day. Try the next day, or pick a different doctor."
                : 'Tap a slot to book it. Slots are 15 minutes long. Times shown in PNG time (Port Moresby).'
          }
        />
        {slotsQ.error && <ErrorBanner error={slotsQ.error} onRetry={() => slotsQ.refetch()} />}
        {slots.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {slots.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => { setReason(''); setPickedSlot(s) }}
                className="text-sm font-semibold bg-wantok-50 hover:bg-wantok-700 hover:text-white border border-wantok-200 text-wantok-800 rounded px-2 py-2 transition"
              >
                {pngTime(s.slot_at)}
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Confirm modal — picks up the slot, asks for an optional reason. */}
      {pickedSlot && (
        <div
          className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-40"
          onClick={() => !book.isPending && setPickedSlot(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl border border-slate-200 max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-xs uppercase tracking-wide text-slate-500">Confirm booking</div>
            <div className="text-lg font-semibold mt-1">
              {pngTime(pickedSlot.slot_at)} — {pngDayLabel(date)}
            </div>
            <div className="text-[11px] text-slate-500">PNG time (Port Moresby)</div>
            <div className="text-sm text-slate-600 mt-0.5">
              with Dr {doctor.full_name || doctor.username}
              {doctor.specialty && ` (${doctor.specialty})`}
            </div>

            <label className="mt-4 block text-xs text-slate-600">
              Reason for visit (optional)
              <textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. persistent cough, follow-up, prescription refill"
                className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
              />
            </label>

            {book.isError && (
              <div className="mt-3">
                <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2 text-sm">
                  {firstError(book.error, 'Could not book the slot. Try another.')}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setPickedSlot(null)}
                disabled={book.isPending}
                className="text-sm border border-slate-200 hover:border-slate-400 rounded px-3 py-2"
              >
                Cancel
              </button>
              <button
                onClick={() => book.mutate()}
                disabled={book.isPending}
                className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
              >
                {book.isPending ? 'Booking…' : 'Confirm booking'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------

function Labeled({ label, children }) {
  return (
    <label className="text-xs text-slate-600 flex flex-col gap-1">
      <span>{label}</span>
      {children}
    </label>
  )
}

